import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { add, bignumber, subtract, multiply } from 'mathjs';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Http } from '@angular/http';

export enum CurrencyType {
  EDG = 'edg',
  BTC = 'btc',
  ETH = 'eth',
  LTC = 'ltc',
  USD = 'usd',
  EUR = 'eur',
  USDT20 = 'usdt20',
  DAI = 'dai',
  DEMO = 'demo',
}

export enum CurrencyMode {
  REAL = 'real',
  BONUS = 'bonus',
}

export enum CurrencyRendering {
  CRYPTO = 'crypto',
  FIAT = 'fiat',
  MIXED = 'mixed',
}

export interface BalanceInterface {
  edg: Partial<BalanceValueInterface>;
  btc: Partial<BalanceValueInterface>;
  eth: Partial<BalanceValueInterface>;
  ltc: Partial<BalanceValueInterface>;
  dai: Partial<BalanceValueInterface>;
  usd: Partial<BalanceValueInterface>;
  usdt20: Partial<BalanceValueInterface>;
}

export interface BalanceValueInterface {
  real: number;
  bonus: number;
  usd: number;
}

export const defaultCurrencyType: CurrencyType = CurrencyType.BTC;
export const currencyList: CurrencyType[] = [
  CurrencyType.EDG,
  CurrencyType.BTC,
  CurrencyType.ETH,
  CurrencyType.LTC,
  CurrencyType.DAI,
  // CurrencyType.USDT20,
];

export const currencyDecimals = {
  edg: 0,
  demo: 0,
  btc: 8,
  ltc: 8,
  eth: 18,
  usd: 2,
  eur: 2,
  dai: 2,
  usdt20: 2,
};

export const microableCurrency = (currency: CurrencyType) => {
  return ![
    CurrencyType.EDG,
    CurrencyType.DEMO,
    CurrencyType.USD,
    CurrencyType.EUR,
    CurrencyType.USDT20,
    CurrencyType.DAI,
  ].includes(currency);
};

export interface BalanceChange {
  real: number;
  bonus: number;
  currency: CurrencyType;
}

const storageCurrencyKey = 'player-currency';
const storageMicroViewer = 'player-currency-micro';
const storageStakeSize = 'player-stake';

@Injectable()
export class BalanceService {
  private _currencyChangeSubject: Subject<CurrencyType> = new BehaviorSubject(this.getCurrency());
  private _currencyMicroSubject: Subject<boolean> = new BehaviorSubject(this.getMicroViewer());
  private _stakeSubject: Subject<number> = new BehaviorSubject(this.getStakeSize());
  public _currencyChange$: Observable<CurrencyType> = this._currencyChangeSubject.asObservable();
  private _microViewerChange$: Observable<boolean> = this._currencyMicroSubject.asObservable();
  private _stakeChange$: Observable<number> = this._stakeSubject.asObservable();

  private _balanceChangeSubject: Subject<BalanceChange> = new BehaviorSubject({
    currency: this.getCurrency(),
    real: this.getRealBalance(this.getCurrency()),
    bonus: this.getBonusBalance(this.getCurrency()),
  });
  private _balanceChange$: Observable<BalanceChange> = this._balanceChangeSubject.asObservable();

  private _currentCurrency: CurrencyType = this.getSavedCurrency();
  private _currencyShowing: CurrencyRendering = CurrencyRendering.MIXED;
  private _usdRates: { [key: string]: number };
  private _microViewer: boolean = false;
  private _stakeSize: number = 1;
  private _balances: Partial<BalanceInterface> = {
    edg: { real: 0, bonus: 0 },
  };
  private _convertedBalances: Partial<BalanceInterface> = {
    edg: { usd: 0 },
  };

  constructor(@Inject(PLATFORM_ID) private platformId: string, private http: Http) {}

  public balanceObservable(): Observable<BalanceChange> {
    return this._balanceChange$;
  }

  public currencyObservable(): Observable<CurrencyType> {
    return this._currencyChange$;
  }

  public microObservable(): Observable<boolean> {
    return this._microViewerChange$;
  }

  public stakeObservable(): Observable<number> {
    return this._stakeChange$;
  }

  public getSavedCurrency(): CurrencyType {
    let currency: CurrencyType;
    if (isPlatformBrowser(this.platformId)) {
      currency = localStorage.getItem(storageCurrencyKey) as CurrencyType;
    }
    return currency || defaultCurrencyType;
  }

  public setCurrency(currency: CurrencyType): void {
    this._currentCurrency = currency;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(storageCurrencyKey, currency);
    }
    this._currencyChangeSubject.next(currency);
  }

  public getCurrency(): CurrencyType {
    return this._currentCurrency || this.getSavedCurrency();
  }

  public getSavedMicroViewer(): boolean {
    let micro: boolean;
    if (isPlatformBrowser(this.platformId)) {
      micro = localStorage.getItem(storageMicroViewer) === 'true';
    }
    return micro || false;
  }

  public getSavedStakeSize(): number {
    let stake: number;
    if (isPlatformBrowser(this.platformId)) {
      stake = Number(localStorage.getItem(storageStakeSize));
    }
    return stake || 1;
  }

  public getMicroViewer(): boolean {
    return this._microViewer || this.getSavedMicroViewer();
  }

  public getStakeSize(): number {
    return this._stakeSize || this.getSavedStakeSize();
  }

  public setMicroViewer(micro: boolean): void {
    this._microViewer = micro;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(storageMicroViewer, String(micro));
    }
    this._currencyMicroSubject.next(micro);
  }

  public setStakeSize(stake: number): void {
    this._stakeSize = stake;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(storageStakeSize, String(stake));
    }
    this._stakeSubject.next(stake);
  }

  public setRenderingMode(rendering: CurrencyRendering): void {
    this._currencyShowing = rendering;
  }

  public updateUsdRates() {
    if (!this._usdRates) {
      this._usdRates = {};
    }
    this.getUsdRates()
      .toPromise()
      .then((response: any) => {
        response = response.json().result;
        response.forEach(item => {
          this._usdRates[item.base.toLowerCase()] = item.rate || 0;
        });
      });
  }

  /**
   * Sets all balances
   * @param balances BalanceInterface
   */
  public setBalances(balances: BalanceInterface): void {
    this._balances = balances;
  }

  /**
   * Set real & bonus balances to specified currency.
   * @param currency CurrencyType
   * @param balance Partial<BalanceValueInterface> { real: 0, bonus: 0 }
   */
  public setBalance(currency: CurrencyType, balance: Partial<BalanceValueInterface>): void {
    const _balance = balance;
    if (!_balance.hasOwnProperty(CurrencyMode.REAL)) {
      _balance.real = 0;
    }
    if (!_balance.hasOwnProperty(CurrencyMode.BONUS)) {
      _balance.bonus = 0;
    }
    this._balances[currency] = _balance;
    this._balanceChangeSubject.next({ currency, real: balance.real, bonus: balance.bonus });
  }

  /**
   * Set real & bonus balances to specified currency.
   * @param currency CurrencyType
   */
  private getUsdRate(currency: CurrencyType) {
    return this.http.get(`/api/config/rates/${currency}`);
  }

  private getUsdRates() {
    return this.http.get(`/api/config/rates`);
  }

  /**
   * @param currency
   */
  public getBalance(currency: CurrencyType): number {
    return Number(add(bignumber(this.getRealBalance(currency)), bignumber(this.getBonusBalance(currency))));
  }

  /**
   * @param currency
   */
  public getRealBalance(currency: CurrencyType): number {
    try {
      return this._balances[currency].real || 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * @param currency
   */
  public getBonusBalance(currency: CurrencyType): number {
    try {
      return this._balances[currency].bonus || 0;
    } catch (e) {
      return 0;
    }
  }

  public getCurrentBalance(): number {
    return this.getBalance(this.getCurrency());
  }

  public getCurrentUsdBalance(): number {
    const currency = this.getCurrency();
    if (!this._usdRates || !this._usdRates[currency]) {
      return 0;
    }
    return Number(multiply(this.getCurrentBalance(), Number(bignumber(this._usdRates[currency]))));
  }

  public renderBalance(): string {
    return `${this.abbreviateNumber(this.getBalance(this.getCurrency()))}`;
  }

  public renderCurrency(amount: number = 0): string {
    return `${this.abbreviateLetter(amount)}${this.getCurrency().toUpperCase()}`;
  }

  public renderRealBalance(): string {
    return `${this.abbreviate(this.getRealBalance(this.getCurrency()))}`;
  }

  public renderBonusBalance(): string {
    return `${this.abbreviate(this.getBonusBalance(this.getCurrency()))}`;
  }

  public currencyNumberFormatter(amount: number): number {
    return Number(subtract(bignumber(amount), bignumber(currencyDecimals[this.getCurrency()])));
  }

  public abbreviateNumber(number: number, digits: number = 2) {
    let expK = Math.floor(Math.log10(Math.abs(number)) / 3);
    let scaled = number / Math.pow(1000, expK);

    if (Math.abs((scaled as any).toFixed(digits)) >= 1000) {
      // Check for rounding to next exponent
      scaled /= 1000;
      expK += 1;
    }

    const SI_SYMBOLS = 'apμm kMGTPE';
    const BASE0_OFFSET = SI_SYMBOLS.indexOf(' ');

    if (expK + BASE0_OFFSET >= SI_SYMBOLS.length) {
      // Bound check
      expK = SI_SYMBOLS.length - 1 - BASE0_OFFSET;
      scaled = number / Math.pow(1000, expK);
    } else if (expK + BASE0_OFFSET < 0) {
      return 0;
    } // Too small

    return scaled.toFixed(digits).replace(/(\.|(\..*?))0+$/, '$2');
  }

  public abbreviateLetter(number: number, digits: number = 2) {
    let expK = Math.floor(Math.log10(Math.abs(number)) / 3);
    const scaled = number / Math.pow(1000, expK);

    if (Math.abs((scaled as any).toFixed(digits)) >= 1000) {
      // Check for rounding to next exponent
      expK += 1;
    }

    const SI_SYMBOLS = 'apμm kMGTPE';
    const BASE0_OFFSET = SI_SYMBOLS.indexOf(' ');

    if (expK + BASE0_OFFSET >= SI_SYMBOLS.length) {
      // Bound check
      expK = SI_SYMBOLS.length - 1 - BASE0_OFFSET;
    } else if (expK + BASE0_OFFSET < 0) {
      return '';
    } // Too small

    return SI_SYMBOLS[expK + BASE0_OFFSET].trim();
  }

  public abbreviate(number: number, digits: number = 2) {
    let expK = Math.floor(Math.log10(Math.abs(number)) / 3);
    let scaled = number / Math.pow(1000, expK);

    if (Math.abs((scaled as any).toFixed(digits)) >= 1000) {
      // Check for rounding to next exponent
      scaled /= 1000;
      expK += 1;
    }

    const SI_SYMBOLS = 'apμm kMGTPE';
    const BASE0_OFFSET = SI_SYMBOLS.indexOf(' ');

    if (expK + BASE0_OFFSET >= SI_SYMBOLS.length) {
      // Bound check
      expK = SI_SYMBOLS.length - 1 - BASE0_OFFSET;
      scaled = number / Math.pow(1000, expK);
    } else if (expK + BASE0_OFFSET < 0) {
      return 0;
    } // Too small

    return scaled.toFixed(digits).replace(/(\.|(\..*?))0+$/, '$2') + SI_SYMBOLS[expK + BASE0_OFFSET].trim();
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/core/balance.service.ts